import { type FC, type MouseEventHandler, useEffect, useCallback, useRef } from 'react';
import { useFragment, useRefetchableFragment, graphql } from 'react-relay';

import { PdpMobileSectionDivider } from '../../PdpMobileSectionDivider/PdpMobileSectionDivider';
import { PdpSharedProductTileCarousel } from '../PdpSharedProductTileCarousel/PdpSharedProductTileCarousel';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import {
    VISUALLY_SIMILAR_ITEMS_LIST,
    trackViewMoreVisuallySimilarClick,
} from '../../tracking/similarItemsTracking';

import { visuallySimilar } from '../../pdpMessages';
import { flattenConnectionNodes } from '../../../utils/relayUtils';

import { type PdpSharedVisuallySimilarItemsCarousel_viewer$key } from './__generated__/PdpSharedVisuallySimilarItemsCarousel_viewer.graphql';
import { type PdpSharedVisuallySimilarItemsCarousel_item$key } from './__generated__/PdpSharedVisuallySimilarItemsCarousel_item.graphql';
import { type PdpSharedVisuallySimilarItemsCarousel_user$key } from './__generated__/PdpSharedVisuallySimilarItemsCarousel_user.graphql';

const viewerFragment = graphql`
    fragment PdpSharedVisuallySimilarItemsCarousel_viewer on Viewer
    @refetchable(queryName: "PdpSharedVisuallySimilarItemsCarouselRefetchQuery")
    @argumentDefinitions(
        isArt: { type: "Boolean", defaultValue: false }
        count: { type: "Int", defaultValue: 10 }
    ) {
        visuallySimilarItems: visualSearch(first: $count, itemPk: $itemId) @include(if: $isArt) {
            edges {
                node {
                    ...PdpSharedProductTileCarousel_items
                        @arguments(
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                }
            }
        }
        ...PdpSharedProductTileCarousel_viewer @arguments(userId: $userId)
    }
`;

const userFragment = graphql`
    fragment PdpSharedVisuallySimilarItemsCarousel_user on User {
        ...PdpSharedProductTileCarousel_user
    }
`;

const itemFragment = graphql`
    fragment PdpSharedVisuallySimilarItemsCarousel_item on Item {
        serviceId
        isArt
    }
`;

const CAROUSEL_CAPACITY = 10;

const PdpSharedVisuallySimilarItemsCarousel: FC<{
    viewer: PdpSharedVisuallySimilarItemsCarousel_viewer$key;
    item: PdpSharedVisuallySimilarItemsCarousel_item$key;
    user?: PdpSharedVisuallySimilarItemsCarousel_user$key | null;
    afterDisplay: boolean;
    currency: string;
}> = ({ viewer: viewerRef, item: itemRef, user: userRef, afterDisplay, currency }) => {
    const [viewer, refetch] = useRefetchableFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const { isMobile } = useServerVarsContext();
    const dataRefetched = useRef(false);

    const { serviceId: itemId, isArt } = item;
    const visuallySimilarItems = flattenConnectionNodes(viewer?.visuallySimilarItems);
    const totalResults = visuallySimilarItems.length;
    // BE bug. visualy similar endpoint doesn't return totalResults. Because of that we can't paginate results properly.
    useEffect(() => {
        if (afterDisplay && !totalResults && isArt && !dataRefetched.current) {
            refetch({ isArt, count: CAROUSEL_CAPACITY });
            dataRefetched.current = true;
        }
    }, [totalResults, isArt, refetch, afterDisplay]);

    const handleViewMoreClick: MouseEventHandler = useCallback(event => {
        trackViewMoreVisuallySimilarClick(event);
    }, []);

    if (!isArt || !visuallySimilarItems.length) {
        return null;
    }

    return (
        <>
            {isMobile && <PdpMobileSectionDivider />}
            <PdpSharedProductTileCarousel
                viewer={viewer}
                items={visuallySimilarItems}
                user={user}
                title={visuallySimilar}
                viewAllLink={`/similar/?item=${itemId}`}
                handleViewAllClick={handleViewMoreClick}
                currency={currency}
                trackingList={VISUALLY_SIMILAR_ITEMS_LIST}
                totalResults={totalResults}
                carouselCapacity={CAROUSEL_CAPACITY}
            />
        </>
    );
};

export default PdpSharedVisuallySimilarItemsCarousel;
